import { EditIcon, TrashIcon } from 'lucide-react';
import type React from 'react';
import { useEffect, useState } from 'react';
import { FormDetailPane } from '@/components/common/FormDetailPane';
import { TextFormField } from '@/components/common/FormField';
import { Button } from '@/components/ui/button';
import { FieldGroup } from '@/components/ui/field';
import { handleApiError } from '../lib/errors';
import { type Customer, type CustomerInput, customerService } from '../services/customerService';
import { logger } from '../utils/logger';

type FormData = CustomerInput;

const emptyForm: FormData = {
  customer_id: '',
  customer_name: '',
  country: '',
  postal_code: '',
  city: '',
  street: '',
  street_no: '',
};

function rowToForm(row: Customer): FormData {
  return {
    customer_id: row.customer_id ?? '',
    customer_name: row.customer_name ?? '',
    country: row.country ?? '',
    postal_code: row.postal_code ?? '',
    city: row.city ?? '',
    street: row.street ?? '',
    street_no: row.street_no ?? '',
  };
}

function formToPayload(form: FormData): CustomerInput {
  const trim = (v: string) => v.trim();
  return {
    customer_id: trim(form.customer_id ?? '') || undefined,
    customer_name: trim(form.customer_name ?? '') || undefined,
    country: trim(form.country ?? '') || undefined,
    postal_code: trim(form.postal_code ?? '') || undefined,
    city: trim(form.city ?? '') || undefined,
    street: trim(form.street ?? '') || undefined,
    street_no: trim(form.street_no ?? '') || undefined,
  };
}

interface CustomerDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (row: Customer) => void;
  onEntrySaved?: () => void;
  row?: Customer | null;
  isEdit?: boolean;
  readOnly?: boolean;
  onEditRequest?: () => void;
  onDeleteRequest?: () => void;
}

export const CustomerDrawer: React.FC<CustomerDrawerProps> = ({
  isOpen,
  onClose,
  onSave,
  onEntrySaved,
  row,
  isEdit = false,
  readOnly = false,
  onEditRequest,
  onDeleteRequest,
}) => {
  const [formData, setFormData] = useState<FormData>(emptyForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      if ((isEdit || readOnly) && row) {
        setFormData(rowToForm(row));
      } else {
        setFormData(emptyForm);
      }
      setSubmitError(null);
    }
  }, [isOpen, isEdit, readOnly, row]);

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const payload = formToPayload(formData);
      const response =
        isEdit && row
          ? await customerService.update(row.id, payload)
          : await customerService.create(payload);
      onSave(response);
      onEntrySaved?.();
      onClose();
    } catch (error) {
      logger.error('Error saving customer:', error);
      setSubmitError(handleApiError(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const fieldsDisabled = readOnly;

  return (
    <FormDetailPane
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={readOnly ? 'View Customer' : isEdit ? 'Edit Customer' : 'Add Customer'}
      description={
        readOnly
          ? 'Customer details.'
          : isEdit
            ? 'Update the customer below.'
            : 'Fill in the customer details.'
      }
      onSubmit={readOnly ? undefined : handleSubmit}
      submitLabel={isEdit ? 'Update' : 'Add'}
      isSubmitting={isSubmitting}
      submitError={submitError}
      showFooter={!readOnly}
    >
      {readOnly ? (
        <div className="flex flex-wrap justify-end gap-2 border-b border-border-default pb-4">
          {onEditRequest ? (
            <Button type="button" size="sm" onClick={onEditRequest}>
              <EditIcon data-icon="inline-start" />
              Edit
            </Button>
          ) : null}
          {onDeleteRequest ? (
            <Button type="button" size="sm" variant="destructive" onClick={onDeleteRequest}>
              <TrashIcon data-icon="inline-start" />
              Delete
            </Button>
          ) : null}
        </div>
      ) : null}
      <FieldGroup>
        <TextFormField
          label="ID"
          value={formData.customer_id ?? ''}
          onChange={(v) => handleChange('customer_id', v)}
          disabled={fieldsDisabled}
          copyable={readOnly}
        />
        <TextFormField
          label="Customer Name"
          value={formData.customer_name ?? ''}
          onChange={(v) => handleChange('customer_name', v)}
          disabled={fieldsDisabled}
          copyable={readOnly}
        />
        <p className="pt-2 text-sm font-medium text-text-secondary">Address</p>
        <TextFormField
          label="Country"
          value={formData.country ?? ''}
          onChange={(v) => handleChange('country', v)}
          disabled={fieldsDisabled}
          copyable={readOnly}
        />
        <TextFormField
          label="Postal Code"
          value={formData.postal_code ?? ''}
          onChange={(v) => handleChange('postal_code', v)}
          disabled={fieldsDisabled}
          copyable={readOnly}
        />
        <TextFormField
          label="City"
          value={formData.city ?? ''}
          onChange={(v) => handleChange('city', v)}
          disabled={fieldsDisabled}
          copyable={readOnly}
        />
        <TextFormField
          label="Street"
          value={formData.street ?? ''}
          onChange={(v) => handleChange('street', v)}
          disabled={fieldsDisabled}
          copyable={readOnly}
        />
        <TextFormField
          label="Street No"
          value={formData.street_no ?? ''}
          onChange={(v) => handleChange('street_no', v)}
          disabled={fieldsDisabled}
          copyable={readOnly}
        />
      </FieldGroup>
    </FormDetailPane>
  );
};
